import api from './api';

/**
 * Client for the Appointment Snapshots report — the day's scheduled deliveries and
 * prepulls as they stood at 8 AM, 12 PM and 4 PM Central, what moved since 8 AM and who
 * moved it, and each order's service failure for the day.
 *
 * Everything but the service failures is READ FROM A SNAPSHOT the Sync job froze at
 * capture time (same-day appointments only, GWI dispatchers only — the Scheduler grid's
 * population). A slot the job slept through comes back `captured: false` and is never
 * back-filled.
 *
 * REJECTED DELIVERIES stay deliveries. An order turned away at the consignee is re-legged
 * (a stopoff parks it at a yard, the delivery moves to a later date), which used to drop
 * it off the day's delivery board and re-list it the same day as a PREPULL. It now stays
 * a DELIVERY on the day it was refused and appears again on its new date, both rows
 * carrying `rejected_date`.
 *
 * ONE TABLE PER SLOT. `rows[slot]` holds that capture's orders; on the 12 PM / 4 PM slots
 * each row carries `change_type` — ADDED when the order was not on the 8 AM board — and
 * the orders that WERE on the 8 AM board but are gone are appended as REMOVED rows. Both
 * compare to the day's FIRST capture, never to the slot before, because the question is
 * always "we had X at 8am, why Y now?". The 8 AM slot has no changes.
 *
 * The server filters on Region / LC / Dispatcher / Customer / Move Type only (the
 * Scheduler page's own lists); everything else is a column on the row and the grid
 * filters it locally. `filters()` takes the current selections and answers FACETED —
 * each dropdown's options are the values that exist in the captured rows under the
 * OTHER dropdowns' picks, so picking Houston leaves only Houston's dispatchers. Extra
 * order fields requested via `additional_fields` are LIVE values from the order as it
 * is now.
 */

export type SnapshotKind = 'DELIVERY' | 'PREPULL';
export type SnapshotChangeType = 'ADDED' | 'REMOVED';
/** Where the leg stood at capture: ARRIVED (destination arrival), STARTED (dispatch started
 *  or origin times), NOT_STARTED (driver, nothing started), NOT_DISPATCHED (no driver).
 *  DISPATCHED / DEPARTED only appear on captures taken before 2026-10-05. '' on a removed row. */
export type SnapshotLegStatus =
  | 'ARRIVED' | 'STARTED' | 'NOT_STARTED' | 'NOT_DISPATCHED'
  | 'DISPATCHED' | 'DEPARTED' | '';

export interface SnapshotSlot {
  /** Central hour the slot represents: 8, 12 or 16. Null on the live card. */
  slot_hour: number | null;
  /** The board for the day as the live tables have it right now — "Current" on today,
   *  "Latest" on a past day — computed on request from ALL orders (so a delivery that
   *  closed since still counts) and diffed against the day's first capture. */
  live: boolean;
  captured: boolean;
  snapshot_id: number | null;
  /** When the capture actually ran (DB clock, Central); a few minutes after the hour.
   *  On the live card, the moment it was computed. */
  captured_at: string | null;
  /** Unfiltered counts stamped on the capture. */
  delivery_count: number;
  prepull_count: number;
  change_count: number;
  /** The capture this slot's changes are measured against: the day's first same-day
   *  capture by default, the chosen one when "Changes since" picked an earlier capture,
   *  or the night-before capture (negative code) on the 8 AM card; null when nothing
   *  earlier exists. */
  baseline_slot_hour: number | null;
  /** The same counts after the Region / LC / Dispatcher / Customer / Move Type filters. */
  filtered_delivery_count: number;
  filtered_prepull_count: number;
  filtered_added: number;
  filtered_removed: number;
}

export interface SnapshotRow {
  id: number;
  snapshot_id: number;
  pronumber: number;
  kind: SnapshotKind;
  legnum: number;
  appt_date: string;
  appt_time_start: string | null;
  appt_time_end: string | null;
  customercode: number | null;
  customer: string;
  csr: string;
  whsecsr: string;
  lc_name: string;
  dispatcher: string;
  region: string;
  movetype: string;
  container: string;
  /** Delivery consignee, or the prepull yard. */
  location: string;
  /** '' on a REMOVED row — it is not on this capture. */
  leg_status: SnapshotLegStatus;
  /** The day a delivery attempt on this order was TURNED AWAY at the consignee, at or
   *  before this row's own appointment date; null when there was none. Equal to
   *  `appt_date` on the day of the refusal itself, earlier on the re-delivery that
   *  follows it. Read live, like the service failures — a refusal logged after a capture
   *  still belongs on that capture's row. Only the re-legged shape counts (the attempt
   *  leg sits before the order's current delivery leg); a REJECTED leg appended after a
   *  delivery that already happened is a different event and is not marked. */
  rejected_date: string | null;

  /** null = on the baseline board and still here. */
  change_type: SnapshotChangeType | null;
  /** Raw cms.history userid; '' when no history row explained the change. */
  changed_by: string;
  changed_by_name: string;
  changed_at: string | null;
  /** cms.history.type — single, SaveRule:*, Azure CMS API, ... */
  change_source: string;
  /** Where the change was made, from `change_source`: "Scheduler", "Manual (360)",
   *  "Manual (V1)", "Change All", "LC Dashboard", "Save rule", "EDI", ... '' when the row did
   *  not change or nothing in history explained it. */
  change_via: string;
  /** What happened, in words: "Appt set", "Appt cleared", "Moved to 10/07", "Order closed", ... */
  note: string;
  old_appt_date: string | null;
  new_appt_date: string | null;
  /** The note folded into a bucket — "Moved to another day", "Appt cleared", "Order closed",
   *  "Appt set", "Unexplained", ... — '' when the row did not change. */
  change_reason: string;
  /** Fell off the board in a way that reads like a reschedule, with no controllable
   *  service failure coded for the day. */
  sf_missing: boolean;
  /** The appointment window closed (plus a 30-minute grace) before the capture / now and
   *  the leg had not arrived — the next service failure in the making. */
  late_risk: boolean;

  /** The order's CONTROLLABLE service failure ("C:" reason) dated the snapshot day,
   *  latest when several; '' / 0 when none. */
  sf_reason: string;
  sf_type: string;
  sf_count: number;
  sf_created_by: string;
  sf_created_by_name: string;
  sf_created_at: string | null;
  sf_comments: string;

  /** Extra order fields requested via `additional_fields` land here, keyed by field name. */
  [extraField: string]: unknown;
}

export interface AppointmentSnapshotsResponse {
  date: string;
  /** The DB's date (Central). The page defaults the picker to this and labels the live
   *  card "Current" when `date === today`, "Latest" otherwise. */
  today: string;
  slot_hours: number[];
  /** Every capture a card can be measured against ("Changes since"), sorted: the day's
   *  own captured slots plus, as a negative code, the night-before capture the job took
   *  of this day's board at the previous day's last slot (-8 = 4 PM the day before, shown
   *  as "Prev 4 PM"). That
   *  one is the 8 AM card's default baseline and is never a card itself. */
  baseline_options: number[];
  snapshots: SnapshotSlot[];
  /** Keyed by slot hour as a string ("8", "12", "16") plus "live" for today's live card;
   *  only captured slots are present. Today's slots whose hour has not come yet are
   *  absent from `snapshots` altogether. */
  rows: Record<string, SnapshotRow[]>;
  fields: string[];
}

/** Every option list is already narrowed to the values the captured rows actually hold,
 *  under the OTHER filters' current selections — the dropdown you are looking at never
 *  hides its own picks. */
export interface AppointmentSnapshotFilters {
  regions: string[];
  /** Groups (tilde-joined member lists, isGroup) then individuals — the Scheduler's list;
   *  a group survives when any of its members has rows. */
  lcs: { value: string; label: string; isGroup: boolean }[];
  customers: { value: string; label: string }[];
  /** Active GWI order dispatchers on a current order — the dispatch pages' roster
   *  without its "(inactive)" entries. */
  dispatchers: { value: string; label: string }[];
  movetypes: { value: string; label: string }[];
  slot_hours: number[];
}

/** What the dropdowns are narrowed by. Same names as the report's own filters. */
export interface AppointmentSnapshotFilterParams {
  terminal?: string;
  lc?: string;
  customer?: string;
  dispatcher?: string;
  movetype?: string;
}

export interface AppointmentSnapshotsParams {
  /** YYYY-MM-DD; the server defaults to today (Central). */
  date?: string;
  terminal?: string;
  lc?: string;
  customer?: string;
  /** Comma-separated order-dispatcher usernames. */
  dispatcher?: string;
  /** Comma-separated move types (IMPORT, EXPORT, ...). */
  movetype?: string;
  /** Slot hour (8 / 12 / 16) the added / removed are measured against; the server
   *  defaults to the day's first capture and falls back to it for a slot that was not
   *  captured or is not earlier than the card being viewed. */
  baseline?: number;
  additional_fields?: string;
  background?: boolean;
}

const lcApptSnapshotsService = {
  async list(params: AppointmentSnapshotsParams = {}): Promise<AppointmentSnapshotsResponse> {
    const res = await api.get<AppointmentSnapshotsResponse>('/lc-appt-snapshots', { params });
    return res.data;
  },

  async filters(params: AppointmentSnapshotFilterParams = {}): Promise<AppointmentSnapshotFilters> {
    const res = await api.get<AppointmentSnapshotFilters>('/lc-appt-snapshots/filters', { params });
    return res.data;
  },
};

export default lcApptSnapshotsService;
